// src/hooks/useCharacterDraft.js
// Единый хук для мастера создания и редактора.
// Даёт компонентам готовые биндинги — никаких useState в компонентах.

import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Gateway from '../api/gateway';
import { createCharacter, updateCharacter } from '../store/slices/gameSlice';
import { registerCharacterInAccess } from '../store/thunks/authThunks';

import {
  CREATION_STEPS,
  MODE_CREATE,
  MODE_EDIT,
  startCreation,
  startEditing,
  resetDraft,
  goToStep,
  patchDraft,
  setRace,
  setTemplateAndTemperament,
  setSkills,
  setField,
  setStepError,
  clearStepError,
  setValidationErrors,
  setSubmitError,
  clearSubmitError,
  generationStarted,
  generationSucceeded,
  generationFailed,
  clearGenerationStatus,
  markSaved,
  revertDraft,
  validateStep,
  validateDraft,
  selectDraft,
  selectDraftMode,
  selectActiveStep,
  selectCurrentStepName,
  selectStepErrors,
  selectValidationErrors,
  selectSubmitError,
  selectGenerationState,
  selectIsDirty,
} from '../store/slices/characterDraftSlice';

export const useCharacterDraft = () => {
  const dispatch = useDispatch();

  const mode = useSelector(selectDraftMode);
  const draft = useSelector(selectDraft);
  const activeStep = useSelector(selectActiveStep);
  const currentStepName = useSelector(selectCurrentStepName);
  const stepErrors = useSelector(selectStepErrors);
  const validationErrors = useSelector(selectValidationErrors);
  const submitError = useSelector(selectSubmitError);
  const generation = useSelector(selectGenerationState);
  const isDirty = useSelector(selectIsDirty);

  // ---------- Жизненный цикл ----------

  const beginCreate = useCallback(() => {
    dispatch(startCreation());
  }, [dispatch]);

  const beginEdit = useCallback(
    (character) => {
      dispatch(startEditing(character));
    },
    [dispatch]
  );

  const reset = useCallback(() => {
    dispatch(resetDraft());
  }, [dispatch]);

  // ---------- Навигация мастера ----------

  const next = useCallback(() => {
    const err = validateStep(currentStepName, draft);
    if (err) {
      dispatch(setStepError({ step: currentStepName, message: err }));
      return false;
    }
    dispatch(clearStepError(currentStepName));
    dispatch(goToStep(Math.min(activeStep + 1, CREATION_STEPS.length - 1)));
    return true;
  }, [dispatch, currentStepName, draft, activeStep]);

  const back = useCallback(() => {
    dispatch(goToStep(Math.max(activeStep - 1, 0)));
  }, [dispatch, activeStep]);

  const goTo = useCallback(
    (step) => {
      dispatch(goToStep(step));
    },
    [dispatch]
  );

  // ---------- Правки черновика ----------

  const patch = useCallback(
    (p) => {
      dispatch(patchDraft(p));
    },
    [dispatch]
  );

  const setFieldValue = useCallback(
    (field, value) => {
      dispatch(setField({ field, value }));
    },
    [dispatch]
  );

  const selectRace = useCallback(
    (race) => {
      dispatch(setRace(race));
    },
    [dispatch]
  );

  const selectTemplate = useCallback(
    (template, temperament) => {
      dispatch(setTemplateAndTemperament({ template, temperament }));
    },
    [dispatch]
  );

  const setSkillsList = useCallback(
    (skills) => {
      dispatch(setSkills(skills));
    },
    [dispatch]
  );

  // ---------- Генерация имени + легенды ----------

  const generateNameAndLegend = useCallback(async () => {
    if (!draft.race || !draft.character_template) {
      dispatch(generationFailed('Сначала выберите расу и шаблон'));
      return null;
    }

    dispatch(generationStarted());
    try {
      const result = await Gateway.api.generation.generateNameAndLegend({
        race_id: draft.race.id,
        character_type: draft.character_template.id,
        gender: draft.gender || 'neutral',
        traits: {
          gender: draft.gender || 'neutral',
          temperament: draft.temperament,
        },
      });

      if (!result || result.status !== 'success') {
        throw new Error(result?.message || 'Генерация вернула ошибку');
      }

      const { name, legend, gender } = result.data || {};
      dispatch(generationSucceeded({ name, legend, gender }));
      return result.data;
    } catch (err) {
      dispatch(
        generationFailed(
          err?.message || Gateway.utils.getErrorMessage(err) || 'Ошибка генерации'
        )
      );
      return null;
    }
  }, [dispatch, draft.race, draft.character_template, draft.gender, draft.temperament]);

  const clearGeneration = useCallback(() => {
    dispatch(clearGenerationStatus());
  }, [dispatch]);

  // ---------- Сохранение ----------

  /**
   * В режиме create — создаёт персонажа через gameSlice.createCharacter.
   * В режиме edit — обновляет через gameSlice.updateCharacter.
   * Возвращает сохранённого персонажа или null.
   */
  const save = useCallback(
    async (userId) => {
      const errors = validateDraft(draft);
      dispatch(setValidationErrors(errors));
      if (errors.length > 0) return null;

      try {
        if (mode === MODE_CREATE) {
          if (!userId) throw new Error('Нет user_id для создания персонажа');

          const payload = {
            name: draft.name,
            race: draft.race?.id || draft.race_id,
            template: draft.character_template?.id || draft.template_id,
            base_temperament: draft.temperament,
            background: draft.background,
            gender: draft.gender,
            selectedSkills: (draft.skills || []).map((s) =>
              typeof s === 'string' ? s : s.id
            ),
            traits: (draft.traits || []).map((t) =>
              typeof t === 'string' ? t : t.id
            ),
            preferences: (draft.preferences || []).map((p) =>
              typeof p === 'string' ? p : p.id
            ),
            phobias: (draft.phobias || []).map((p) =>
              typeof p === 'string' ? p : p.id
            ),
            appearance: draft.appearance || {},
            user_id: userId,
          };

          const created = await dispatch(createCharacter(payload)).unwrap();
          dispatch(markSaved(created));

          if (created?.id) {
            dispatch(
              registerCharacterInAccess({
                characterId: created.id,
                source: 'created',
              })
            );
          }

          return created;
        }

        // mode === 'edit'
        if (!draft.id) throw new Error('Нет id персонажа для сохранения');

        const updates = {
          name: draft.name,
          race: draft.race?.id || draft.race_id,
          template: draft.character_template?.id || draft.template_id,
          temperament: draft.temperament,
          background: draft.background,
          skills: (draft.skills || []).map((s) =>
            typeof s === 'string' ? s : s.id
          ),
          appearance: draft.appearance || {},
        };

        const updated = await dispatch(
          updateCharacter({ characterId: draft.id, updates })
        ).unwrap();
        dispatch(markSaved(updated));
        return updated;
      } catch (err) {
        const msg =
          typeof err === 'string'
            ? err
            : err?.message || Gateway.utils.getErrorMessage(err);
        dispatch(setSubmitError(msg));
        return null;
      }
    },
    [dispatch, draft, mode]
  );

  const revert = useCallback(() => {
    dispatch(revertDraft());
  }, [dispatch]);

  const clearSubmit = useCallback(() => {
    dispatch(clearSubmitError());
  }, [dispatch]);

  // ---------- Возврат ----------

  return {
    // состояние
    mode,
    draft,
    activeStep,
    currentStepName,
    stepErrors,
    validationErrors,
    submitError,
    generation,
    isDirty,

    // проверки
    validateStep: (stepName) => validateStep(stepName, draft),
    validateDraft: () => validateDraft(draft),

    // жизненный цикл
    beginCreate,
    beginEdit,
    reset,

    // навигация
    next,
    back,
    goTo,

    // правки
    patch,
    setFieldValue,
    selectRace,
    selectTemplate,
    setSkillsList,

    // генерация
    generateNameAndLegend,
    clearGeneration,

    // сохранение
    save,
    revert,
    clearSubmit,
  };
};

export default useCharacterDraft;